import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

const API_BASE = process.env.REACT_APP_API_URL || "";

const TYPES = [
  { key: "docx", label: "Word (DOCX)", accept: ".docx" },
];

const authHeaders = () => {
  const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;

/**
 * Enterprise default templates: one reusable template per file type. Used automatically when a user asks for a
 * document of that type without attaching or choosing another template. The backend enforces admin-only access and
 * enterprise isolation; this section is only rendered on the (admin) Enterprise Settings page.
 */
const EnterpriseDefaultTemplates = ({ enterpriseId, sectionNumber, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const inputs = useRef({});

  const base = `${API_BASE}/enterprise-templates/${enterpriseId}`;

  const load = useCallback(async () => {
    if (!enterpriseId) return;
    setLoading(true);
    try {
      const res = await api.get(base, { headers: authHeaders() });
      const next = {};
      (res.data?.templates || []).forEach((t) => {
        next[t.file_type] = t.template_id ? t : null;
      });
      setRows(next);
    } catch (err) {
      showErrorToast(errorMessage(err, "Failed to load default templates"));
    } finally {
      setLoading(false);
    }
  }, [base, enterpriseId]);

  useEffect(() => {
    load();
  }, [load]);

  const setBusyFor = (key, value) => setBusy((prev) => ({ ...prev, [key]: value }));

  const upload = async (type, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(`.${type.key}`)) {
      showErrorToast(`Please choose a ${type.accept} file`);
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setBusyFor(type.key, true);
    try {
      await api.put(`${base}/${type.key}`, form, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      showSuccessToast(`${type.label} default template saved`);
      await load();
    } catch (err) {
      showErrorToast(errorMessage(err, "Failed to save default template"));
    } finally {
      setBusyFor(type.key, false);
      if (inputs.current[type.key]) inputs.current[type.key].value = "";
    }
  };

  const remove = async (type) => {
    if (!window.confirm(`Remove the ${type.label} default template? Future documents will use the standard layout.`)) return;
    setBusyFor(type.key, true);
    try {
      await api.delete(`${base}/${type.key}`, { headers: authHeaders() });
      showSuccessToast(`${type.label} default template removed`);
      await load();
    } catch (err) {
      showErrorToast(errorMessage(err, "Failed to remove default template"));
    } finally {
      setBusyFor(type.key, false);
    }
  };

  const download = async (type) => {
    try {
      const res = await api.get(`${base}/${type.key}/download`, { headers: authHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = rows[type.key]?.filename || `default-template.${type.key}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showErrorToast("Failed to download default template");
    }
  };

  return (
    <>
      <div
        className="section-header d-flex align-items-center gap-2 mb-2"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      >
        <div className="d-flex align-items-center">
          <div className="section-badge me-2">{sectionNumber}</div>
          <h5 className="section-title mb-0">Default Document Templates</h5>
        </div>
        <i className={`bi ${open ? "bi-caret-up-fill" : "bi-caret-down-fill"}`}></i>
      </div>

      {open && (
        <div className="glossary-panel mb-3">
          <p className="text-muted mb-3">
            Upload the default Word template for this enterprise. When someone asks for a document without attaching or choosing a template,
            the document is written into it, keeping its layout, fonts, colors, logos, headers and footers. A template attached to the
            request always takes priority. Replacing it keeps the earlier versions, and documents already created keep the version they
            were made with.
          </p>
          {loading && <div className="text-muted mb-2">Loading…</div>}
          <div className="table-responsive p-0">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Current template</th>
                  <th>Last updated</th>
                  <th style={{ width: 360 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {TYPES.map((type) => {
                  const row = rows[type.key];
                  return (
                    <tr key={type.key}>
                      <td>{type.label}</td>
                      <td>{row ? row.filename : <span className="text-muted">Not set (standard layout is used)</span>}</td>
                      <td>{row?.updated_at ? new Date(row.updated_at).toLocaleString() : "—"}</td>
                      <td>
                        <div className="glossary-actions">
                          <input
                            type="file"
                            accept={type.accept}
                            style={{ display: "none" }}
                            ref={(el) => (inputs.current[type.key] = el)}
                            onChange={(e) => upload(type, e.target.files?.[0])}
                          />
                          <button
                            type="button"
                            className="custom-btn"
                            disabled={!!busy[type.key]}
                            onClick={() => inputs.current[type.key]?.click()}
                          >
                            {busy[type.key] ? "Working..." : row ? "Replace" : "Upload"}
                          </button>
                          {row && (
                            <>
                              <button type="button" className="custom-btn" onClick={() => download(type)}>
                                Download
                              </button>
                              <button
                                type="button"
                                className="cancle-btn"
                                disabled={!!busy[type.key]}
                                onClick={() => remove(type)}
                              >
                                Remove
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default EnterpriseDefaultTemplates;
